import { removeExtension } from "@/components/ui-v2/FilePicker/components/FilePickerContent/components/Poser/utils";
import { FilepickerFile } from "@/components/ui-v2/FilePicker/types";
import { ContentType, ObjectType, VideoFrame } from "@/types";

export const getHasAudio = (videoElement: HTMLVideoElement) => {
  const video = videoElement as any;

  const hasAudio =
    // HTML5 AudioTrackList API
    (video.audioTracks && video.audioTracks.length > 0) ||
    // Mozilla specific property
    video.mozHasAudio === true ||
    // Webkit audio byte count (Safari/Chrome)
    (video.webkitAudioDecodedByteCount !== undefined &&
      video.webkitAudioDecodedByteCount > 0);

  return hasAudio;
};

const FRAME_QUALITY = 0.8;

export const ensureVideoReady = async (
  video: HTMLVideoElement,
): Promise<void> => {
  if (video.readyState >= 2) {
    return;
  }

  return new Promise((resolve, reject) => {
    const handleLoadedMetadata = () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
      resolve();
    };

    const handleError = () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
      reject(new Error("Failed to load video metadata"));
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("error", handleError);
  });
};

export const extractFrameFromVideo = async (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  time: number,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    // Ensure video is ready before proceeding
    ensureVideoReady(video)
      .then(() => {
        video.currentTime = time;

        const handleSeeked = () => {
          video.removeEventListener("seeked", handleSeeked);
          video.removeEventListener("error", handleSeekError);

          // Use video dimensions or fallback to canvas dimensions
          const width = video.videoWidth || video.width || canvas.width;
          const height = video.videoHeight || video.height || canvas.height;

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(video, 0, 0);

          try {
            const frameSrc = canvas.toDataURL("image/jpeg", FRAME_QUALITY);
            resolve(frameSrc);
          } catch {
            reject(new Error("Failed to extract frame from video"));
          }
        };

        const handleSeekError = () => {
          video.removeEventListener("seeked", handleSeeked);
          video.removeEventListener("error", handleSeekError);
          reject(new Error("Failed to seek to specified time in video"));
        };

        video.addEventListener("seeked", handleSeeked);
        video.addEventListener("error", handleSeekError);
      })
      .catch(reject);
  });
};

// Find the closest frame to current time
export const getCurrentFrame = (frames: VideoFrame[], currentTime: number) => {
  if (frames.length === 0) return null;

  // Binary search for the closest frame
  let left = 0;
  let right = frames.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midTimestamp = frames[mid].timestamp;

    if (midTimestamp === currentTime) {
      return frames[mid];
    }

    if (midTimestamp < currentTime) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  // Handle edge cases where binary search goes out of bounds
  const leftFrame = right >= 0 ? frames[right] : frames[0];
  const rightFrame =
    left < frames.length ? frames[left] : frames[frames.length - 1];

  const leftDistance = Math.abs(leftFrame.timestamp - currentTime);
  const rightDistance = Math.abs(rightFrame.timestamp - currentTime);

  return leftDistance <= rightDistance ? leftFrame : rightFrame;
};

// Get evenly distributed frames for display in video scrubber
export const getVisibleFrames = (
  frames: VideoFrame[],
  numFramesToShow: number,
) => {
  if (frames.length === 0) return [];

  if (numFramesToShow >= frames.length) {
    return frames;
  }

  const indices = Array.from({ length: numFramesToShow }, (_, i) => {
    if (i === 0) return 0; // First frame always at beginning
    if (i === numFramesToShow - 1) return frames.length - 1; // Last frame always at end

    const spacing = (frames.length - 1) / (numFramesToShow - 1);
    return Math.round(spacing * i);
  });

  return indices.map((index) => frames[index]);
};

export const convertVideoFileToFilepickerFile = (
  file: File,
): Promise<FilepickerFile> => {
  return new Promise<FilepickerFile>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const dataUri = reader.result as string;

      // Create a temporary video element to get dimensions
      const video = document.createElement("video");
      video.preload = "metadata";

      const cleanUp = () => {
        video.onloadedmetadata = null;
        video.onloadeddata = null;
        video.onerror = null;
        video.src = "";
      };

      const createFile = (width?: number, height?: number): FilepickerFile => ({
        id: crypto.randomUUID(),
        src: dataUri,
        name: removeExtension(file.name),
        type: "video" as ObjectType,
        contentType: file.type as ContentType,
        thumbnailSrc: dataUri,
        interactedAt: new Date(),
        ...(width && height ? { width, height } : {}),
      });

      const handleLoadedMetadata = () => {
        // Check if dimensions are actually available
        if (video.videoWidth && video.videoHeight) {
          const width = video.videoWidth;
          const height = video.videoHeight;
          cleanUp();
          const createdFile = createFile(width, height);
          resolve(createdFile);
        } else {
          // If dimensions aren't available yet, wait for loadeddata event
          video.onloadeddata = () => {
            if (video.videoWidth && video.videoHeight) {
              const width = video.videoWidth;
              const height = video.videoHeight;
              cleanUp();
              resolve(createFile(width, height));
            } else {
              cleanUp();
              resolve(createFile());
            }
          };
        }
      };

      const handleError = () => {
        cleanUp();
        resolve(createFile());
      };

      video.onloadedmetadata = handleLoadedMetadata;
      video.onerror = handleError;
      video.src = dataUri;
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsDataURL(file);
  });
};
